"use client"

import * as React from "react"
import { TrendingUp, TrendingDown } from "lucide-react"
import { Cell, Label, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts"

// Helper function to detect browser
const detectBrowser = () => {
  if (typeof window === 'undefined') return 'Other';
  
  const userAgent = window.navigator.userAgent;
  
  if (userAgent.indexOf("Chrome") > -1 && userAgent.indexOf("Edg") === -1) {
    return "Chrome";
  } else if (userAgent.indexOf("Safari") > -1 && userAgent.indexOf("Chrome") === -1) {
    return "Safari";
  } else if (userAgent.indexOf("Firefox") > -1) {
    return "Firefox";
  } else if (userAgent.indexOf("Edg") > -1) {
    return "Edge";
  } else {
    return "Other";
  }
};

// Browser colors
const browserColors = {
  "Chrome": "#4285F4",
  "Safari": "#34A853",
  "Firefox": "#FBBC05",
  "Edge": "#EA4335",
  "Other": "#673AB7"
};

// Simple card components
const Card = ({ className, children }) => (
  <div className={`bg-white rounded-lg border border-gray-200 shadow-sm ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ className, children }) => (
  <div className={`p-6 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children }) => (
  <h3 className="text-xl font-semibold text-gray-900">
    {children}
  </h3>
);

const CardDescription = ({ children }) => (
  <p className="text-sm text-gray-500">
    {children}
  </p>
);

const CardContent = ({ className, children }) => (
  <div className={`px-6 ${className}`}>
    {children}
  </div>
);

const CardFooter = ({ className, children }) => (
  <div className={`p-6 border-t border-gray-200 ${className}`}>
    {children}
  </div>
);

// Custom tooltip component
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border border-gray-200 shadow-md rounded">
        <p className="font-medium">{payload[0].name}: {payload[0].value} visitors</p>
      </div>
    );
  }
  return null;
};

export default function Component() {
  const [visitorData, setVisitorData] = React.useState([]);
  const [totalVisitors, setTotalVisitors] = React.useState(0);
  const [trend, setTrend] = React.useState({ percentage: 0, direction: 'up' });
  const [lastUpdated, setLastUpdated] = React.useState('');

  // Get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  // Get current month and year
  const getCurrentMonthYear = () => {
    const now = new Date();
    return `${now.toLocaleString('default', { month: 'long' })} ${now.getFullYear()}`;
  };

  // Initialize data on first load
  React.useEffect(() => {
    const initializeVisitorData = () => {
      // Try to get existing data from localStorage
      const storedData = localStorage.getItem('visitorData');
      let data = storedData ? JSON.parse(storedData) : {};
      
      // Detect current browser
      const browser = detectBrowser();
      const today = getCurrentDate();
      
      // Initialize today's date if it doesn't exist
      if (!data[today]) {
        data[today] = {
          Chrome: 0,
          Safari: 0, 
          Firefox: 0,
          Edge: 0,
          Other: 0,
          total: 0
        };
      }
      
      // Increment visitor count for the detected browser
      data[today][browser] += 1;
      data[today].total += 1;
      
      // Save updated data to localStorage
      localStorage.setItem('visitorData', JSON.stringify(data));
      
      // Calculate trend (comparing to yesterday)
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;
      
      let trendValue = 0;
      let trendDirection = 'up';
      
      if (data[yesterdayStr] && data[yesterdayStr].total > 0) {
        const percentChange = ((data[today].total - data[yesterdayStr].total) / data[yesterdayStr].total) * 100;
        trendValue = Math.abs(percentChange).toFixed(1);
        trendDirection = percentChange >= 0 ? 'up' : 'down';
      }
      
      // Transform data for the chart
      const chartData = Object.keys(browserColors).map(browserName => ({
        browser: browserName,
        visitors: data[today][browserName] || 0,
        color: browserColors[browserName]
      }));
      
      // Update state
      setVisitorData(chartData);
      setTotalVisitors(data[today].total);
      setTrend({ percentage: trendValue, direction: trendDirection });
      setLastUpdated(new Date().toLocaleTimeString());
    };

    initializeVisitorData();
  }, []);

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <CardTitle>Real-Time Visitor Tracker</CardTitle>
        <CardDescription>{getCurrentMonthYear()} - Updated at {lastUpdated}</CardDescription>
      </CardHeader>
      <CardContent className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip content={<CustomTooltip />} />
            <Pie
              data={visitorData}
              dataKey="visitors"
              nameKey="browser"
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={80}
              strokeWidth={3}
              stroke="#fff"
            >
              {visitorData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
              <Label
                content={({ viewBox }) => {
                  const { cx, cy } = viewBox;
                  return (
                    <g>
                      <text
                        x={cx}
                        y={cy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="text-2xl font-bold"
                        fill="#333"
                      >
                        {totalVisitors.toLocaleString()}
                      </text>
                      <text
                        x={cx}
                        y={cy + 24}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="text-sm"
                        fill="#666"
                      >
                        Visitors
                      </text>
                    </g>
                  );
                }}
              />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
      <CardFooter className="text-center">
        <div className="flex items-center justify-center gap-2 font-medium mb-2">
          <span>
            {trend.direction === 'up' ? 'Trending up' : 'Trending down'} by {trend.percentage}% today
          </span>
          {trend.direction === 'up' ? (
            <TrendingUp className="h-4 w-4 text-green-500" />
          ) : (
            <TrendingDown className="h-4 w-4 text-red-500" />
          )}
        </div>
        <div className="text-sm text-gray-500">
          Browser usage tracked in real-time by visitor
        </div>
      </CardFooter>
    </Card>
  );
}